import { Button } from "react-aria-components";

import {
  freshTaskStatusLabel,
  isFreshTaskActive,
  type ConversationFreshTask,
} from "@/features/chat/conversationFreshTasks";

interface Props {
  onReadAgain: () => void;
  stale: boolean;
  tasks: ReadonlyArray<ConversationFreshTask>;
}

export function ConversationFreshTaskStatus({ onReadAgain, stale, tasks }: Props) {
  const visible = [...tasks]
    .sort((left, right) => {
      const priority = Number(isFreshTaskActive(right)) - Number(isFreshTaskActive(left));
      return priority || Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
    })
    .slice(0, 8);
  if (visible.length === 0 && !stale) return null;

  return (
    <section
      aria-label="当前对话的 CWM 现采任务"
      className="mx-auto mb-5 w-full max-w-[1440px] rounded-[24px] border border-white/80 bg-white/65 px-6 py-5 shadow-[0_10px_28px_rgba(27,47,59,0.07)]"
      data-testid="conversation-fresh-tasks"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">CWM 现采</h2>
          <p className="mt-1 text-sm text-ink-muted">状态来自当前对话的持久任务，不依据回答文字推断。</p>
        </div>
        {stale || visible.some((task) => task.cancelPending || task.cancelRequestUnknown || task.retryableReadError) ? (
          <Button
            className="rounded-full bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700"
            onPress={onReadAgain}
          >
            重新读取状态
          </Button>
        ) : null}
      </header>
      {stale ? (
        <p className="mt-3 text-sm text-red-700" role="alert">
          现采状态暂未同步，以下仅为最后一次已读取的结果。
        </p>
      ) : null}
      {visible.length > 0 ? (
        <ul className="mt-4 grid gap-3">
          {visible.map((task) => (
            <li
              className="rounded-2xl border border-slate-200/75 bg-white/70 px-4 py-3"
              data-fresh-task-status={task.status}
              key={task.id}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <strong className="text-sm text-ink" aria-live="polite">
                  {freshTaskStatusLabel(task)}
                </strong>
                <time className="text-xs text-ink-muted" dateTime={task.updatedAt}>
                  最近同步 {formatTime(task.updatedAt)}
                </time>
              </div>
              <p className="mt-1 break-all text-xs text-ink-muted">任务 ID：{task.id}</p>
              {task.knowledgeCutoff ? (
                <p className="mt-1 text-xs text-ink-muted">
                  来源：CWM 现采 · 数据截止 {formatTime(task.knowledgeCutoff)}
                </p>
              ) : (
                <p className="mt-1 text-xs text-ink-muted">来源：CWM 现采</p>
              )}
              {task.retryableReadError ? (
                <p className="mt-1 text-xs text-amber-700">
                  {freshReadErrorText(task.errorCode)}；任务尚未终止。可重新读取当前状态，不能新建第二个现采任务。
                </p>
              ) : null}
              {task.cancelRequestUnknown && isFreshTaskActive(task) ? (
                <p className="mt-1 text-xs text-amber-700">
                  本地已保存取消请求，但不能证明 CWM 已接收；请读取原任务状态，不要重复提交。
                  {task.cancelRequestedAt ? ` · 请求时间 ${formatTime(task.cancelRequestedAt)}` : ""}
                </p>
              ) : task.cancelPending && isFreshTaskActive(task) ? (
                <p className="mt-1 text-xs text-amber-700">
                  CWM 已接收取消请求，但尚未证明采集停止；请读取当前任务终态，不要直接重复提交。
                  {task.cancelRequestedAt ? ` · 请求时间 ${formatTime(task.cancelRequestedAt)}` : ""}
                </p>
              ) : isFreshTaskActive(task) ? (
                <p className="mt-1 text-xs text-ink-muted">
                  如需停止，请在对话中明确提出取消并引用此任务 ID；页面不会仅取消本地记录。
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function freshReadErrorText(code: string) {
  switch (code) {
    case "cwm_gateway_unavailable": return "CWM 服务暂时不可用";
    case "cwm_gateway_contract_mismatch": return "CWM 返回了不符合当前协议的数据";
    case "cwm_gateway_response_oversize": return "CWM 返回内容超过读取上限";
    default: return "本次未取得可信的现采状态";
  }
}

function formatTime(value: string) {
  const milliseconds = Date.parse(value);
  if (!Number.isFinite(milliseconds)) return "时间未知";
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(milliseconds);
}
